import { useMemo } from "react"

// ---------------------------------------------------------------------------
// Dev-only mount seam for the in-call screen (AT-17).
//
// The in-call layout is pure geometry, but the only production route into it is
// a live Twilio room — and the ruled verification for the mobile screen-share
// layout is fake-media Playwright, deliberately NOT the gated live lane. This
// module mounts the real `InCall` with synthetic tracks, so what a browser test
// measures is the shipped component and not a copy of it.
//
// Inert unless BOTH hold: the build is a dev build, and the URL asks for it.
// Vite replaces `import.meta.env.DEV` with a literal `false` in a production
// build, which collapses the mode lookup below to `useMemo(() => null, [])` —
// verified in the built bundle, where the string "call_preview" does not appear
// at all. The seam therefore cannot be entered from a deployed release even with
// the query string. A few unreachable literals survive minification; they are
// dead weight, not a live path.
// ---------------------------------------------------------------------------

// A widescreen share against 4:3 cameras: the pair that made the original
// `cover` crop visible, and the pair that proves `contain` letterboxes. The
// intrinsic sizes are also how a browser test tells the three tracks apart, so
// the cameras deliberately avoid 640x480 — Chromium's fake capture device.
const SCREEN = { width: 1920, height: 1080, color: "#1e3a8a" }
const CAMERA = { width: 800, height: 600, color: "#334155" }

// `VideoSurface` only ever calls attach()/detach() on a track, so a canvas-backed
// <video> is a complete stand-in. It carries real intrinsic dimensions, which is
// what object-fit needs in order to have something to fit.
function syntheticTrack({ width, height, color }) {
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext("2d")
  context.fillStyle = color
  context.fillRect(0, 0, width, height)

  const stream = canvas.captureStream(1)
  const attached = []

  return {
    kind: "video",
    attach() {
      const el = document.createElement("video")
      el.autoplay = true
      el.muted = true
      el.playsInline = true
      el.srcObject = stream
      attached.push(el)
      return el
    },
    detach() {
      return attached.splice(0, attached.length)
    },
  }
}

/**
 * Pass the live room through untouched, except in a dev build visited with
 * `?call_preview=remote` (the freelancer is sharing) or `?call_preview=local`
 * (you are sharing, so the stage holds the placeholder instead of a track).
 *
 * The live hook keeps running underneath — it stays in `prejoin` internally and
 * opens the camera preview it always would. That is the price of the seam being
 * one line at the call site rather than a second code path through the page.
 */
export default function useCallPreview(live) {
  const mode = useMemo(() => {
    if (!import.meta.env.DEV || typeof window === "undefined") return null
    const value = new URLSearchParams(window.location.search).get("call_preview")
    return value === "remote" || value === "local" ? value : null
  }, [])

  // Built once per mode: `VideoSurface` keys its attach effect on track identity,
  // so a track rebuilt each render would tear the video element down every time.
  const tracks = useMemo(
    () =>
      mode === null
        ? null
        : {
            local: syntheticTrack(CAMERA),
            remote: syntheticTrack(CAMERA),
            screen: mode === "remote" ? syntheticTrack(SCREEN) : null,
          },
    [mode]
  )

  if (!tracks) return live

  return {
    ...live,
    phase: "incall",
    roomCode: "Preview-000000",
    timeLeft: 1800,
    timeLeftLabel: "30:00",
    // No thread, so the page's chat poll stays off and the panel has nothing to
    // fetch — the preview is about layout, not transport.
    conversationSid: null,
    micOn: true,
    camOn: true,
    screenOn: true,
    localVideoTrack: tracks.local,
    remoteScreenTrack: tracks.screen,
    participants: [
      {
        identity: "Preview Freelancer",
        videoTrack: tracks.remote,
        audioTrack: null,
        cameraOff: false,
        muted: false,
      },
    ],
  }
}
